import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const slides = [
    {
      id: 1,
      title: "Salary Advance",
      highlight: "For Professionals",
      subtitle: "Instant disbursal | Zero paperwork.",
    },
    {
      id: 2,
      title: "Flexible Tenure",
      highlight: "Customizable Options",
      subtitle: "Repayment terms designed for salaried employees.",
    },
    {
      id: 3,
      title: "Digital First",
      highlight: "100% Paperless",
      subtitle: "Complete the process online in under 2 minutes.",
    },
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 4000);

    return () => clearInterval(interval);
  }, [slides.length]);

  return (
    <section className="relative w-full min-h-screen bg-[#0A1620] overflow-hidden">
      {/* PREMIUM DARK GRADIENT BACKGROUND */}
      <div className="absolute inset-0">
        {/* Base Dark Color */}
        <div className="absolute inset-0 bg-[#0A1620]"></div>

        {/* Deep Blue-Green Accent - Top Right */}
        <div className="absolute -top-40 -right-40 w-[600px] h-[600px] bg-[#1B4D5E]/20 rounded-full blur-[120px]"></div>

        {/* Navy Accent - Bottom Left */}
        <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-[#1A3B4D]/20 rounded-full blur-[100px]"></div>

        {/* Very Subtle Grid Pattern */}
        <div className="absolute inset-0 opacity-[0.03]">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `linear-gradient(to right, #2D5F7A 1px, transparent 1px), linear-gradient(to bottom, #2D5F7A 1px, transparent 1px)`,
              backgroundSize: "60px 60px",
            }}
          ></div>
        </div>

        {/* TOP WAVE - Upper Side */}
        <svg
          className="absolute top-0 left-0 w-full h-48 rotate-180"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="#2D5F7A"
            fillOpacity="0.08"
            d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>

        {/* BOTTOM WAVE - Lower Side */}
        <svg
          className="absolute bottom-0 right-0 w-full h-48"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
        >
          <path
            fill="#2D5F7A"
            fillOpacity="0.08"
            d="M0,192L48,176C96,160,192,128,288,128C384,128,480,160,576,176C672,192,768,192,864,176C960,160,1056,128,1152,128C1248,128,1344,160,1392,176L1440,192L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>
      </div>

      {/* MAIN CONTENT - Full Width Layout */}
      <div className="relative z-20 h-full flex items-center bg-transparent">
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 w-full py-24 lg:py-22">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            {/* LEFT SIDE - Animated Text Content */}
            <div className="space-y-6 rounded-2xl animate-fade-up">
              {/* Premium Badge */}
              <div
                key={`badge-${currentSlide}`}
                className="inline-flex items-center gap-2 bg-[#1E3B4A]/30 px-4 py-2 rounded-full border border-[#2D5F7A]/30 backdrop-blur-sm"
              >
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#4D9ABF] opacity-40"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4D9ABF]"></span>
                </span>
                <span className="text-[#B2D9F0] text-xs font-bold uppercase tracking-wider">
                  Disbursal in minutes
                </span>
              </div>

              {/* Headline - Animated */}
              <div key={`title-${currentSlide}`} className="space-y-2">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight">
                  {slides[currentSlide].title}
                  <span className="block mt-2">
                    <span className="relative z-10 text-transparent bg-clip-text bg-gradient-to-r from-[#7BC4E0] to-[#4D9ABF]">
                      {slides[currentSlide].highlight}
                    </span>
                  </span>
                </h1>
              </div>

              {/* Subtitle */}
              <div key={`subtitle-${currentSlide}`} className="mt-2">
                <p className="text-xl text-[#CCE6F5] font-light leading-relaxed">
                  {slides[currentSlide].subtitle}
                </p>
              </div>

              {/* Feature Pills - Professional */}
              <div className="flex flex-wrap gap-3 mt-8">
                <div className="flex items-center gap-2 bg-[#1E3B4A]/20 backdrop-blur-sm px-4 py-2.5 rounded-lg border border-[#2D5F7A]/20">
                  <svg className="w-4 h-4 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-[#CCE6F5] text-sm font-medium">
                    Instant Approval
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-[#1E3B4A]/20 backdrop-blur-sm px-4 py-2.5 rounded-lg border border-[#2D5F7A]/20">
                  <svg className="w-4 h-4 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
                  </svg>
                  <span className="text-[#CCE6F5] text-sm font-medium">
                    100% Digital
                  </span>
                </div>
                <div className="flex items-center gap-2 bg-[#1E3B4A]/20 backdrop-blur-sm px-4 py-2.5 rounded-lg border border-[#2D5F7A]/20">
                  <svg className="w-4 h-4 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span className="text-[#CCE6F5] text-sm font-medium">
                    No Paperwork
                  </span>
                </div>
              </div>

              {/* CTA Buttons */}
              <div className="flex gap-4 mt-10">
                <Link
                  to="/apply-now"
                  className="group px-8 py-4 bg-gradient-to-r from-[#2D5F7A] to-[#1E3B4A] text-white font-semibold text-base rounded-lg shadow-lg hover:shadow-[#2D5F7A]/30 transition-all duration-300 hover:scale-105 flex items-center gap-2 border border-[#7BC4E0]/20"
                >
                  Check Eligibility
                  <svg
                    className="w-4 h-4 group-hover:translate-x-1 transition-transform"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                  </svg>
                </Link>

                <Link
                  to="/contact"
                  className="px-8 py-4 bg-transparent border border-[#2D5F7A] text-[#CCE6F5] font-semibold text-base rounded-lg hover:bg-[#1E3B4A]/30 transition-all duration-300"
                >
                  Speak to Advisor
                </Link>
              </div>

              {/* Trust Indicators */}
              <div className="flex items-center gap-6 mt-10 pt-6 border-t border-[#2D5F7A]/20">
                <div className="flex -space-x-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="w-8 h-8 rounded-full bg-gradient-to-br from-[#2D5F7A] to-[#1E3B4A] border-2 border-[#0A1620] flex items-center justify-center">
                      <span className="text-[#CCE6F5] text-xs font-bold">{i}</span>
                    </div>
                  ))}
                </div>
                <span className="text-[#8FAFC7] text-sm">
                  Trusted by salaried professionals across India
                </span>
              </div>
            </div>

            {/* RIGHT SIDE - Professional Illustration */}
            <div className="relative flex items-center justify-center">
              <div className="relative w-full max-w-md">
                {/* Decorative Elements */}
                <div className="absolute inset-0 bg-gradient-to-r from-[#2D5F7A]/20 to-[#1E3B4A]/20 rounded-[32px] blur-2xl"></div>
                
                {/* Main Card */}
                <div className="relative bg-gradient-to-br from-[#1A2F3A] to-[#0F2128] rounded-[24px] shadow-2xl overflow-hidden border border-[#2D5F7A]/30 p-8">
                  {/* Abstract Professional Illustration */}
                  <div className="aspect-square w-full relative">
                    {/* Circular Graph Elements */}
                    <svg className="w-full h-full" viewBox="0 0 200 200">
                      {/* Background Circle */}
                      <circle cx="100" cy="100" r="80" fill="none" stroke="#2D5F7A" strokeWidth="1" strokeDasharray="4 4" opacity="0.3"/>
                      
                      {/* Growth Chart Line */}
                      <path
                        d="M40 140 L80 100 L120 70 L160 40"
                        stroke="#7BC4E0"
                        strokeWidth="3"
                        fill="none"
                        strokeLinecap="round"
                      />
                      
                      {/* Data Points */}
                      <circle cx="40" cy="140" r="4" fill="#7BC4E0" />
                      <circle cx="80" cy="100" r="4" fill="#7BC4E0" />
                      <circle cx="120" cy="70" r="4" fill="#7BC4E0" />
                      <circle cx="160" cy="40" r="4" fill="#7BC4E0" />
                      
                      {/* Bar Chart Elements */}
                      <rect x="30" y="160" width="20" height="30" fill="#2D5F7A" opacity="0.6" rx="4" />
                      <rect x="60" y="140" width="20" height="50" fill="#2D5F7A" opacity="0.6" rx="4" />
                      <rect x="90" y="120" width="20" height="70" fill="#2D5F7A" opacity="0.6" rx="4" />
                      <rect x="120" y="100" width="20" height="90" fill="#2D5F7A" opacity="0.6" rx="4" />
                      <rect x="150" y="80" width="20" height="110" fill="#2D5F7A" opacity="0.6" rx="4" />
                      
                      {/* Professional Network Dots */}
                      <g opacity="0.2">
                        <circle cx="180" cy="20" r="2" fill="#CCE6F5"/>
                        <circle cx="20" cy="180" r="2" fill="#CCE6F5"/>
                        <circle cx="180" cy="180" r="2" fill="#CCE6F5"/>
                        <circle cx="50" cy="50" r="2" fill="#CCE6F5"/>
                      </g>
                    </svg>

                   
                   
                   

                    <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                      <div className="w-16 h-16 rounded-full bg-[#1E3B4A] flex items-center justify-center border-2 border-[#7BC4E0]/30">
                        <svg className="w-8 h-8 text-[#7BC4E0]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Stats */}
                 
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Indicators */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex gap-2 z-30">
        {slides.map((_, index) => (
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`transition-all duration-500 rounded-full ${
              index === currentSlide
                ? "w-8 h-2 bg-[#7BC4E0]"
                : "w-2 h-2 bg-[#2D5F7A]/40 hover:bg-[#2D5F7A]/60"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
    </section>
  );
};

export default HeroSection;